import { SorterResult } from 'antd/es/table/interface'
import { DrilldownTablePanelData } from 'features/drilldown/components/table/DrilldownTable'
import {
  PaginatedRolodexTableProps,
  PaginatedRolodexTableRecordType,
} from 'features/drilldown/components/table/PaginatedRolodexTable'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useNestedSetter } from 'features/drilldown/utils/useNestedSetter'
import {
  OptionalValueSetterPair,
  ValueSetterPair,
} from 'features/drilldown/utils/valueSetterPair'
import { OntologyRolodexEntryType, useOntology } from 'features/dynamicOntology'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'
import { RolodexHistogramFilter } from 'types'

import { DrilldownTableColumnDef } from './DrilldownTableSchema'

export type DrilldownTableExtraColumn = {
  id: string
  name: string
  linked_types?: ReadonlyArray<OntologyRolodexEntryType>
  sortKey?: {
    field?: string
    key?: string
    type?: string
  }
  width?: string | number
  render: FC<{ rec: PaginatedRolodexTableRecordType }>
  /**
   * Plain-text equivalent of `render`, used for Excel/CSV export.
   * Columns without an `exportValue` will appear blank in the export.
   */
  exportValue?: (rec: PaginatedRolodexTableRecordType) => string
}

export type DrilldownPaginatedTableOptions = Pick<
  PaginatedRolodexTableProps,
  'expandRecords' | 'tableRowKey'
> & {
  defaultPageSize?: number
}

type TableConfigContextType = OptionalValueSetterPair<
  OntologyRolodexEntryType,
  'groupByType'
> &
  OptionalValueSetterPair<
    ReadonlyArray<DrilldownTableColumnDef>,
    'columnDefs'
  > &
  OptionalValueSetterPair<boolean, 'showFocusColumn'> &
  ValueSetterPair<boolean, 'propagateColumnFilters'> &
  OptionalValueSetterPair<
    Readonly<Record<string, string>>,
    'columnFilterValues'
  > &
  OptionalValueSetterPair<ReadonlyArray<string>, 'selectedRowKeys'> &
  OptionalValueSetterPair<ReadonlyArray<string>, 'enabledExtraColumnIds'> & {
    extraColumns: ReadonlyArray<DrilldownTableExtraColumn>
  } & {
    initialSorts: ReadonlyArray<SorterResult<PaginatedRolodexTableRecordType>>
  } & {
    additionalRolodexFilters: ReadonlyArray<RolodexHistogramFilter> | undefined
    paginatedTableOptions: DrilldownPaginatedTableOptions | undefined
  }

const DrilldownTableConfigContext = createContext<TableConfigContextType>({
  groupByType: 'unit',
  setGroupByType: undefined,
  columnDefs: [],
  setColumnDefs: undefined,
  showFocusColumn: false,
  setShowFocusColumn: undefined,
  propagateColumnFilters: false,
  setPropagateColumnFilters: () => {},
  columnFilterValues: {},
  setColumnFilterValues: undefined,
  selectedRowKeys: [],
  setSelectedRowKeys: undefined,
  extraColumns: [],
  enabledExtraColumnIds: [],
  setEnabledExtraColumnIds: () => {},
  initialSorts: [],
  additionalRolodexFilters: undefined,
  paginatedTableOptions: undefined,
})

export interface DrilldownTableConfigContextProviderProps {
  allowSetGroupByType?: boolean
  allowSetColumns?: boolean
  initialSortingOpts?: ReadonlyArray<
    SorterResult<PaginatedRolodexTableRecordType>
  >
  extraColumns: ReadonlyArray<DrilldownTableExtraColumn>
  /**
   * Filters appended to this panel's own table query without being
   * propagated downstream. Use this when you need to scope the table
   * but do NOT want sibling/later panels to inherit the filter.
   * (For propagating filters, call `useFilters(...)` instead.)
   */
  additionalRolodexFilters?: ReadonlyArray<RolodexHistogramFilter>
  paginatedTableOptions?: DrilldownPaginatedTableOptions
}

export const DrilldownTableConfigContextProvider: FC<
  DrilldownTableConfigContextProviderProps & { children: ReactNode }
> = ({
  allowSetGroupByType,
  allowSetColumns,
  children,
  extraColumns,
  initialSortingOpts,
  additionalRolodexFilters,
  paginatedTableOptions,
}) => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<DrilldownTablePanelData>()
  const {
    groupByType,
    columns: columnDefs,
    showFocusColumn,
    propagateColumnFilters,
    columnFilterValues,
    selectedRowKeys,
    enabledExtraColumnIds,
  } = panelData
  const setGroupByType = useNestedSetter(setPanelData, 'groupByType')
  const setColumnDefs = useNestedSetter(setPanelData, 'columns')
  const setShowFocusColumn = useNestedSetter(setPanelData, 'showFocusColumn')
  const setPropagateColumnFilters = useNestedSetter(
    setPanelData,
    'propagateColumnFilters'
  )
  const setColumnFilterValues = useNestedSetter(
    setPanelData,
    'columnFilterValues'
  )
  const setSelectedRowKeys = useNestedSetter(setPanelData, 'selectedRowKeys')
  const setEnabledExtraColumnIds = useNestedSetter(
    setPanelData,
    'enabledExtraColumnIds'
  )

  const { fields } = useOntology()
  const safeColumnDefs = useMemo(
    () => (columnDefs ?? []).filter((cd) => !!fields[cd.field]),
    [columnDefs, fields]
  )

  return (
    <DrilldownTableConfigContext.Provider
      value={{
        groupByType,
        setGroupByType: allowSetGroupByType ? setGroupByType : undefined,
        columnDefs: safeColumnDefs,
        setColumnDefs: allowSetColumns ? setColumnDefs : undefined,
        showFocusColumn,
        setShowFocusColumn,
        propagateColumnFilters,
        setPropagateColumnFilters,
        columnFilterValues,
        setColumnFilterValues,
        selectedRowKeys,
        setSelectedRowKeys,
        enabledExtraColumnIds,
        setEnabledExtraColumnIds,
        extraColumns,
        initialSorts: initialSortingOpts ?? [],
        additionalRolodexFilters,
        paginatedTableOptions,
      }}>
      {children}
    </DrilldownTableConfigContext.Provider>
  )
}

export function useDrilldownTableConfig(): TableConfigContextType {
  return useContext(DrilldownTableConfigContext)
}
